import editicon from "../../../../assets/img/schooladmin/icons/edit.png";
import deleteicon from "../../../../assets/img/schooladmin/icons/delete.png";
import { Link, useNavigate } from "react-router-dom";
import DeleteModal from "../../../common/DeleteModal";
import { useState } from "react";

/**
 * @typedef {Object} TeacherTableRowProps
 * @property {Object} teacher - The teacher object
 * @property {(teacherId: string) => Promise<void>} onDelete - Function to delete a teacher
 * @property {boolean} deleteLoading - Whether delete is in progress
 * @property {string} deleteError - Error message from delete operation
 * @property {boolean} deleteSuccess - Whether delete was successful
 * @property {() => void} resetDeleteState - Function to reset delete state
 * @property {() => void} onDeleteSuccess - Callback after successful deletion
 */

/**
 * @param {TeacherTableRowProps} props
 */
const TeacherTableRow = ({
  teacher,
  onDelete,
  deleteLoading,
  deleteError,
  deleteSuccess,
  resetDeleteState,
  onDeleteSuccess,
  RoleGuard,
}) => {
  const navigate = useNavigate();
  const fullName = `${teacher?.firstName} ${teacher?.lastName}`;
  const [showModal, setShowModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleShow = () => {
    resetDeleteState();
    setShowModal(true);
  };

  const handleClose = () => {
    resetDeleteState();
    setShowModal(false);
    setIsDeleting(false);
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    await onDelete(teacher.id);
  };

  const handleSuccessContinue = () => {
    setShowModal(false);
    setIsDeleting(false);
    resetDeleteState();
    onDeleteSuccess();
  };

  // Only show loading/error/success for this specific row
  const showLoading = isDeleting && deleteLoading;
  const showError = isDeleting && deleteError;
  const showSuccess = isDeleting && deleteSuccess;

  return (
    <tr
      key={teacher.id}
      style={{
        cursor: "pointer",
      }}
      onClick={() => {
        navigate(`/school-admin/school/report/teachers/${teacher?.id}`);
      }}
    >
      <td>
        <div className="custom-name-cell">
          <span className="custom-name">{fullName}</span>
        </div>
      </td>
      <td>{teacher.email}</td>
      <td className="custom-id">{teacher?.assigned_students_count}</td>
      <td className="custom-progress-cell">
        {teacher?.status === "1" ? (
          <span className="badge bg-success">Active</span>
        ) : (
          <span className="badge bg-secondary">Inactive</span>
        )}
      </td>
      <RoleGuard permission="teachers_classrooms">
        <td onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            onClick={() =>
              navigate(`/school-admin/classroom?teacherId=${teacher.id}`)
            }
            style={{
              backgroundColor: "#000",
              color: "#fff",
              border: "none",
              padding: "6px 14px",
              borderRadius: "6px",
              cursor: "pointer",
              fontSize: "13px",
              fontWeight: "500",
              whiteSpace: "nowrap",
            }}
          >
            View Classrooms
          </button>
        </td>
      </RoleGuard>

      <RoleGuard permission="teachers_edit_delete">
        <td onClick={(e) => e.stopPropagation()}>
          <RoleGuard permission="teachers_edit">
            <Link
              to={`/school-admin/teachers/edit/${teacher.id}`}
              className="tchrdashboard-tableicn"
            >
              <img src={editicon} alt="" />
            </Link>
          </RoleGuard>

          <RoleGuard permission="teachers_delete">
            <Link
              onClick={() => handleShow()}
              className="tchrdashboard-edittableicn"
            >
              <img src={deleteicon} alt="" />
            </Link>
          </RoleGuard>
          <DeleteModal
            showModal={showModal}
            handleClose={handleClose}
            message={`Are you sure you want to delete ${fullName}? This action cannot be undone.`}
            loading={showLoading}
            handleDelete={handleDelete}
            error={showError ? deleteError : ""}
            success={showSuccess}
            successMessage={`${fullName} has been deleted successfully.`}
            onSuccessContinue={handleSuccessContinue}
          />
        </td>
      </RoleGuard>
    </tr>
  );
};

export default TeacherTableRow;
